/* eslint-disable @typescript-eslint/no-namespace */
import { Fluent2WebLightTheme } from '@fluentui/fluent2-theme';
import type { ITheme } from '@fluentui/react';
import { mergeThemes } from '@fluentui/react';
import type { IM365ExtendedSemanticColors } from '@m365-admin/customizations';

import { getFluent2ComponentStyles } from './fluent2-components-styles';

namespace fluent2LightPalette {
  // Warnings and errors
  export const errorText = '#990000';
  export const errorBackground = '#FDE7E9';
  export const blockingBackground = '#FDE7E9';
  export const warningBackground = '#FFF4CE';
  export const warningHighlight = '#FFB900';
  export const successBackground = '#DFF6DD';

  // Extended colors

  export const severeWarningBackground = '#C53601'; // deprecated - no longer used
  export const severeWarningIcon = '#DE590B';

  export const overlayBackground = 'rgba(0,0,0,.4)';
  export const navBackground = '#F0F0F0';

  export const dataVis1 = '#637CEF';
  export const dataVis2 = '#E3008C';
  export const dataVis3 = '#2AA0A4';
  export const dataVis4 = '#9373C0';
  export const dataVis5 = '#13A10E';
  export const dataVis6 = '#3A96DD';
  export const dataVis7 = '#CA5010';
  export const dataVis8 = '#57811B';
  export const dataVis9 = '#B146C2';
  export const dataVis10 = '#AE8C00';

  export const criticalStatus = '#610000';
  export const highStatus = '#B10E1C';
  export const mediumStatus = '#BC4B09';
  export const lowStatus = '#F7630C';
  export const goodStatus = '#107C10';
  export const unknownStatus = '#E0E0E0';
  export const insightLow = '#768DF1';
  export const insightMedium = '#4F6BED';
  export const insightHigh = '#3B52B4';
  export const neutralRisk = '#DADADA';

  export const needHelpBackground = '#00838C';
  export const needHelpBackgroundPressed = '#006B73';

  export const whiteTranslucent40 = 'rgba(255,255,255,.4)';

  export const tagBackgroundLowImpact = '#F3F2F1';
  export const tagBackgroundRecommendation = '#D3F4ED';
  export const tagBackgroundInsight = '#C7D3FF';

  export const grey26 = '#424242';
  export const grey38 = '#616161';
  export const grey74 = '#BDBDBD';

  export const green10 = '#0E700E';

  export const red10 = '#BC2F32';
}

const p = Fluent2WebLightTheme.palette;
const m365p = fluent2LightPalette;

const extendedSemanticColors: Partial<IM365ExtendedSemanticColors> = {
  inputBorder: p.neutralQuaternary,

  // Checkbox
  inputBackgroundChecked: p.themePrimary,
  inputBackground: p.white,
  bodyTextChecked: p.neutralPrimary,
  inputBackgroundCheckedHovered: p.themeDarkAlt,

  // Errors and warnings
  errorText: m365p.errorText,
  warningHighlight: m365p.warningHighlight,
  warningText: p.neutralPrimary,

  // Message bar colors
  messageText: p.neutralPrimary,
  messageLink: p.themeDarkAlt,
  messageLinkHovered: p.themeDarker,

  infoIcon: p.neutralSecondary,
  errorIcon: m365p.highStatus,
  blockingIcon: m365p.highStatus,
  successIcon: m365p.goodStatus,
  warningIcon: p.neutralPrimary,
  severeWarningIcon: m365p.severeWarningIcon,

  infoBackground: p.neutralLighter,
  errorBackground: m365p.errorBackground,
  blockingBackground: m365p.blockingBackground,
  warningBackground: m365p.warningBackground,
  successBackground: m365p.successBackground,

  ////////////////////////////////
  // Extended colors
  ////////////////////////////////

  dashboardBackdrop: p.neutralLighter,
  actionCardDashboardBorder: 'transparent', // no need in light theme since we have shadows
  actionCardDashboardBorderHovered: 'transparent', // no need in light theme since we have shadows
  gridItemDestinationBackground: p.neutralQuaternary,
  panelBackground: p.white, // same as bodyBackground in this theme
  overlayBackground: m365p.overlayBackground,

  // Divider lines
  sectionDividerLine: p.neutralPrimary,
  primaryDividerLine: p.neutralTertiaryAlt,
  secondaryDividerLine: p.neutralQuaternaryAlt,

  // Data vis
  dataVis1: m365p.dataVis1,
  dataVis2: m365p.dataVis2,
  dataVis3: m365p.dataVis3,
  dataVis4: m365p.dataVis4,
  dataVis5: m365p.dataVis5,
  dataVis6: m365p.dataVis6,
  dataVis7: m365p.dataVis7,
  dataVis8: m365p.dataVis8,
  dataVis9: m365p.dataVis9,
  dataVis10: m365p.dataVis10,

  // Text
  headerText: p.black,
  secondaryListText: p.neutralSecondary,

  // Nav
  navBackground: m365p.navBackground,
  navItemBackgroundHovered: p.neutralQuaternary,
  navItemBackgroundPressed: p.neutralTertiaryAlt,

  // Status
  criticalStatus: m365p.criticalStatus,
  highStatus: m365p.highStatus,
  mediumStatus: m365p.mediumStatus,
  lowStatus: m365p.lowStatus,
  goodStatus: m365p.goodStatus,
  unknownStatus: m365p.grey26,
  insightLow: m365p.insightLow,
  insightMedium: m365p.insightMedium,
  insightHigh: m365p.insightHigh,

  neutralRisk: m365p.neutralRisk,

  // Steps
  stepCompleted: p.themePrimary,
  stepCurrent: p.themePrimary,
  stepNotStarted: m365p.grey26,
  stepModifierBorder: p.themePrimary,
  stepHover: p.themeDark,
  stepPressed: p.themeDarker,
  stepError: m365p.highStatus,
  allStepsComplete: m365p.goodStatus,

  // Help + Feedback
  needHelpForeground: p.white,
  needHelpBackground: m365p.needHelpBackground,
  needHelpBackgroundPressed: m365p.needHelpBackgroundPressed,
  feedbackForeground: p.white,
  feedbackBackground: p.neutralDark,
  feedbackBackgroundPressed: p.neutralSecondary,

  // Because the Fabric controls bank on theme.palette.white/black inverting.
  alwaysWhite: p.white,
  alwaysBlack: p.black,

  // InPageFilter
  filterPillRest: p.neutralLighter,
  filterPillHover: p.neutralLight,
  filterPillPressed: p.neutralQuaternaryAlt,
  filterPillEditedRest: p.themeLighter,
  filterPillEditedHover: p.themeLight,
  filterPillEditedPressed: p.themeTertiary,

  // Tag
  tagBackgroundInsight: '#C8D1FA',
  tagBackgroundLowImpact: '#F0F0F0',
  tagBackgroundMediumImpact: p.neutralQuaternary,
  tagBackgroundHighImpact: '#292929',
  tagBackgroundNew: p.themePrimary,
  tagBackgroundActiveWarning: '#D13438',
  tagBackgroundRecommendation: '#A6E9ED',
  tagTextInsight: p.neutralPrimary,
  tagTextLowImpact: p.neutralPrimary,
  tagTextMediumImpact: p.black,
  tagTextHighImpact: p.white,
  tagTextNew: p.white,
  tagTextActiveWarning: p.white,

  // Grey values
  inputIconHovered: m365p.grey26,
  inputPlaceholderText: m365p.grey38,

  disabledBorder: m365p.grey74,
  disabledText: m365p.grey74,
};

export const M365Fluent2LightTheme: ITheme = mergeThemes(Fluent2WebLightTheme, {
  semanticColors: extendedSemanticColors,
  components: getFluent2ComponentStyles,
});
